import React from 'react';

import { APP_ROUTES } from 'constants/routes';
import {
  clearStoredConsent,
  getConsentCookie,
  getUpdatedStoredConsent,
  setConsentCookie,
  updateConsentStatus,
  updateStoredConsent
} from 'helpers/ConsentHelpers';
import { useAnalytics } from 'hooks/useAnalytics';
import { useConsentStatus } from 'hooks/useConsentStatus';
import { useDeepCompareEffect } from 'hooks/useDeepCompareEffect';
import { useGlobalVariables } from 'hooks/useGlobalVariables';
import { useIsOpen } from 'hooks/useIsOpen';

import ConsentBanner from '../ConsentBanner/ConsentBanner';
import ConsentModal from '../ConsentModal/ConsentModal';

import type { IConsent } from 'types';

const ConsentManager = () => {

  /**
   * Refs
   */

  const isCookieBannerOpenedRef = React.useRef(false);
  const isConsentStatusSetRef = React.useRef(false);

  /**
   * Hooks
   */

  const analytics = useAnalytics();

  const { setFunction } = useGlobalVariables();

  const { isOpen: isConfigModalOpen, open: openConfigModal, close: closeConfigModal } = useIsOpen();
  const { isOpen: isBannerOpen, open: openBanner, close: closeBanner } = useIsOpen();

  const [shouldOnlyConfigureNewCookies, setShouldOnlyConfigureNewCookies] = React.useState(false);

  // Data Hooks

  const {
    //
    shouldStoreConsentLocally,
    shouldSyncConsent,
    shouldShowConsentBanner,
    shouldSetCookiePolicy,
    //
    defaultConsent,
    localConsent,
    remoteConsent,
    cookiePolicy,
    //
    updateRemoteConsent,
    //
  } = useConsentStatus();

  /**
   * Props
  */

  const isActive = isValidPath();
  const initialConsentCookie = React.useMemo(() => getConsentCookie(), []);

  // --- Local Consent

  const {
    timestamp: localTimestamp,
    cookies: localCookies,
  } = localConsent || {};

  const {
    hasGivenConsent: hasGivenRemoteConsent
  } = remoteConsent || {};

  /**
   * Handlers
   */

  const handleSetCookiePolicy = React.useCallback((consent: IConsent) => {
    setConsentCookie(consent);
    analytics.setConsentStatus();
  }, []);

  const handleUpdateLocalConsent = React.useCallback((consent: IConsent, onSuccess?: () => void) => {
    const updatedConsent = getUpdatedStoredConsent(consent);

    updateStoredConsent(updatedConsent);
    handleSetCookiePolicy(updatedConsent);

    isConsentStatusSetRef.current = true;

    if (onSuccess) {
      onSuccess();
    }
  }, []);

  const handleUpdateRemoteConsent = React.useCallback((consent: IConsent, onSuccess?: () => void, onError?: () => void) => {
    updateRemoteConsent({
      data: {
        timestamp: consent?.timestamp,
        cookies: consent?.cookies?.map(({ slug, isAccepted }) => ({ slug, isAccepted })),
      },
      onSuccess: () => {
        isConsentStatusSetRef.current = false;
        updateConsentStatus();
        if (onSuccess) {
          onSuccess();
        }
      },
      onError: () => {
        if (onError) {
          onError();
        }
      }
    });
  }, []);

  const handleUpdateConsent = React.useCallback((consent: IConsent, onSuccess?: () => void, onError?: () => void) => {
    // Update local consent
    if (shouldStoreConsentLocally) {
      handleUpdateLocalConsent(consent, onSuccess);

    // Update remote consent
    } else {
      handleUpdateRemoteConsent(consent, onSuccess, onError);
    }
  }, [shouldStoreConsentLocally, handleUpdateLocalConsent, handleUpdateRemoteConsent]);

  const handleOpenCookieModal = React.useCallback((onlyConfigureNewCookies?: boolean) => {
    setShouldOnlyConfigureNewCookies(onlyConfigureNewCookies || false);
    openConfigModal();
  }, [openConfigModal]);

  /**
   * Effects
   */

  // --- Set helper function

  React.useEffect(() => {
    setFunction('showCookiePreferences', (e: any) => {
      e?.preventDefault();
      handleOpenCookieModal();
      return false;
    });
  }, [setFunction, handleOpenCookieModal]);

  // --- Clear stored consent if remote consent has been given

  React.useEffect(() => {
    if (isActive && hasGivenRemoteConsent && !shouldStoreConsentLocally) {
      clearStoredConsent();
    }
  }, [isActive, hasGivenRemoteConsent, shouldStoreConsentLocally]);

  // --- Sync local and remote consent

  useDeepCompareEffect(() => {
    if (isActive && shouldSyncConsent) {
      handleUpdateConsent(
        {
          timestamp: localTimestamp,
          cookies: localCookies,
          hasGivenConsent: true,
          shouldRequestConsent: false
        },
        () => {
          clearStoredConsent();
        }
      );
    }
  }, [isActive, shouldSyncConsent, localTimestamp, localCookies, handleUpdateConsent]);

  // --- Show the cookie banner

  useDeepCompareEffect(() => {
    if (isActive && shouldShowConsentBanner && !isCookieBannerOpenedRef.current) {
      isCookieBannerOpenedRef.current = true;
      openBanner();
    }
  }, [isActive, shouldShowConsentBanner, cookiePolicy, handleUpdateConsent, handleOpenCookieModal]);

  // --- Set default cookie policy

  useDeepCompareEffect(() => {
    if (isActive && defaultConsent && !initialConsentCookie) {
      setConsentCookie(defaultConsent);
    }
  }, [isActive, defaultConsent, initialConsentCookie]);

  // --- Set cookie policy

  useDeepCompareEffect(() => {
    if (isActive && shouldSetCookiePolicy && !isConsentStatusSetRef.current) {
      isConsentStatusSetRef.current = true;
      handleSetCookiePolicy(cookiePolicy);
    }
  }, [isActive, shouldSetCookiePolicy, cookiePolicy, initialConsentCookie, handleSetCookiePolicy]);

  // Props

  const modalCookies = shouldOnlyConfigureNewCookies
    ? cookiePolicy?.cookies?.filter(({ isNew, hasSetConsent }) => isNew && !hasSetConsent)
    : cookiePolicy?.cookies;

  /**
   * Render
   */

  return (
    <>
      <ConsentBanner
        isOpen={isBannerOpen}
        cookies={cookiePolicy?.cookies}
        onUpdateConsent={handleUpdateConsent}
        onClose={closeBanner}
        onConfigureClick={() => {
          handleOpenCookieModal(true);
        }}
      />
      <ConsentModal
        //
        isOpen={isConfigModalOpen}
        cookies={modalCookies}
        //
        onConfirmClick={handleUpdateConsent}
        onClose={closeConfigModal}
        //
      />
    </>
  );
};

// Helpers

function isValidPath() {
  const { pathname } = window.location;
  return (
    !pathname.startsWith(APP_ROUTES.LOGIN)
    && !pathname.startsWith(APP_ROUTES.LOGOUT)
    && !pathname.startsWith(APP_ROUTES.NEWSLETTER_UNSUBSCRIBE)
  );
}

// Export

export default ConsentManager;
